import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["truncated", "$use", "$disabled", "ExternalIconName"],
  _excluded2 = ["children", "disabled", "external", "ExternalIcon", "href", "iconProps", "linkRef", "hrefLang", "onClick", "onKeyDown", "rel", "role", "tabIndex", "target", "to", "truncate", "truncateStringProps", "underline", "use"];
import unescapedText from 'I18n/utils/unescapedText';
import { memo, useRef } from 'react';
import styled from 'foundations-theming/utils';
import devLogger from 'react-utils/devLogger';
import { getRel, getTarget, warnIfShouldBeExternal, renderSecurityTaggedLink } from './Link.utils';
import { getSafeUrl } from '../../utils/unsafeUrl';
import { getTabIndex } from '../../utils/getTabIndex';
import { ExternalLinkIcon } from 'foundations-assets';
import { getColorTokensForUse, getExternalIconColorTokensForUse } from './Link.styles';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { TruncateString } from '../TruncateString';
import { useTagContext } from '../Tag/TagProvider';
import { deepMerge } from '../../utils/deepMerge/deepMerge';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledIconWrapper = styled.span.withConfig({
  displayName: "Link__StyledIconWrapper",
  componentId: "trdfg6-0"
})(({
  theme
}) => {
  return {
    marginInline: theme.space['100'],
    display: 'inline-block',
    verticalAlign: 'middle'
  };
});
const StyledExternalIcon = styled(props => {
  const {
      $use,
      $disabled,
      ExternalIconName
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const IconName = ExternalIconName || ExternalLinkIcon;
  let use = $use;
  if ($disabled) use = 'disabled';
  return /*#__PURE__*/_jsx(StyledIconWrapper, {
    children: /*#__PURE__*/_jsx(IconName, Object.assign({
      color: getExternalIconColorTokensForUse(use)
    }, rest))
  });
}).withConfig({
  displayName: "Link__StyledExternalIcon",
  componentId: "trdfg6-1"
})([""]);
// ${({ truncated }) => truncated && truncatedExternalIconMixin};

export const StyledLink = styled.a.withConfig({
  displayName: "Link__StyledLink",
  componentId: "trdfg6-2"
})(({
  theme,
  $alwaysUnderline,
  $cssStyles,
  $disabled,
  $use,
  $styleOverrides
}) => {
  const useColorTokens = getColorTokensForUse($use);
  const unstyled = $use === 'unstyled';
  return deepMerge(Object.assign({
    // The border radius is always applied so that there aren't any border radius changes when the focus ring appears
    borderRadius: theme.borderRadius['100'],
    display: 'inline',
    fontWeight: !unstyled ? theme.typography['heading-25'].fontWeight : theme.typography['body-100'].fontWeight,
    transition: 'all 0.1s ease-in'
  }, $disabled ? {
    color: theme.color[useColorTokens.disabled],
    cursor: 'not-allowed',
    '&:hover': {
      color: theme.color[useColorTokens.disabled],
      cursor: 'not-allowed',
      textDecoration: 'none'
    }
  } : {
    color: theme.color[useColorTokens.idle],
    cursor: 'pointer',
    textDecoration: $alwaysUnderline && !unstyled ? 'underline solid' : 'inherit',
    '&:hover': {
      color: theme.color[useColorTokens.hover],
      textDecoration: unstyled ? 'inherit' : 'underline solid'
    },
    '&:active': {
      color: theme.color[useColorTokens.active],
      textDecoration: unstyled ? 'inherit' : 'underline solid'
    }
  }, $cssStyles), $styleOverrides);
});
export const MemolessLink = _ref => {
  var _tagContext$linkStyle;
  let {
      children,
      disabled = false,
      external = false,
      ExternalIcon,
      href,
      iconProps,
      linkRef,
      hrefLang,
      onClick,
      onKeyDown,
      rel,
      role,
      tabIndex,
      target,
      // @ts-expect-error - Property 'to' does not exist on type 'LinkProps & { children?: ReactNode; }'
      to,
      truncate = false,
      truncateStringProps,
      underline = false,
      use = 'dark'
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded2);
  if (process.env.NODE_ENV !== 'production') {
    if (to != null) {
      devLogger.warn({
        message: `Link: The \`to\` prop is a no-op. Did you mean \`href\`? (\`to="${to}"\`)`,
        key: 'Link: to'
      });
    }
  }
  const defaultLinkRef = useRef(null);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const tagContext = useTagContext();
  const handleClick = evt => {
    if (disabled) {
      evt.preventDefault();
      evt.stopPropagation();
    } else {
      if (onClick) onClick(evt);
    }
  };
  const handleKeyDown = evt => {
    // Simulate click on Spacebar, to emulate button behavior
    const {
      key
    } = evt;
    if (onKeyDown) onKeyDown(evt);
    if (!onClick) return;

    // Ignore keydown events that bubbled up
    if (evt.target !== (linkRef || defaultLinkRef).current) return;
    if (key === ' ') {
      if (disabled) {
        evt.preventDefault();
        evt.stopPropagation();
        return;
      }
      // @ts-expect-error - KeyboardEvent is not the same as MouseEvent
      onClick(evt);

      // Prevent scrolling
      evt.preventDefault();
    }
  };
  const computedTarget = getTarget(external, target);
  const computedRel = getRel(rel, computedTarget);
  const computedRole = href ? undefined : role || 'button';
  const externalLinkIcon = external ? /*#__PURE__*/_jsx(StyledExternalIcon, Object.assign({
    "aria-label": unescapedText('salesUI.UILink.externalTitle'),
    $use: use,
    $disabled: disabled,
    ExternalIconName: ExternalIcon,
    name: "externalLink"
  }, iconProps, {
    role: "img"
  })) : null;
  if (process.env.NODE_ENV !== 'production') {
    warnIfShouldBeExternal(external, computedTarget, 'Link');
  }
  const wrappedChildren = truncate ? /*#__PURE__*/_jsx(TruncateString, Object.assign({
    useFlex: true,
    fixedChildren: externalLinkIcon
  }, truncateStringProps, {
    children: children
  })) : children;
  const renderedLink = /*#__PURE__*/_jsxs(StyledLink, Object.assign({
    onClick: handleClick,
    onKeyDown: handleKeyDown
  }, domProps, {
    $alwaysUnderline: underline || use === 'on-bright' || use === 'on-dark',
    $use: use,
    $disabled: disabled,
    $cssStyles: cssStyles,
    $styleOverrides: (_tagContext$linkStyle = tagContext === null || tagContext === void 0 ? void 0 : tagContext.linkStyleOverrides) !== null && _tagContext$linkStyle !== void 0 ? _tagContext$linkStyle : {},
    $truncate: truncate,
    href: disabled ? undefined : getSafeUrl(href),
    ref: linkRef || defaultLinkRef,
    rel: computedRel,
    role: computedRole,
    tabIndex: getTabIndex(disabled, tabIndex),
    target: computedTarget,
    "aria-disabled": disabled,
    "data-link": true,
    hrefLang: hrefLang,
    children: [wrappedChildren, truncate ? null : externalLinkIcon]
  }));
  return renderSecurityTaggedLink(href, renderedLink);
};
export const Link = /*#__PURE__*/memo(MemolessLink);